import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  UserPlus, Loader2, ShieldCheck, Shield, Eye, RotateCcw, KeyRound,
  Power, Copy, Check, X, Pencil, Trash2, Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  superadmin: { label: "Superadmin", cls: "bg-black text-[#0fe814]", icon: ShieldCheck },
  admin: { label: "Admin", cls: "bg-[#0fe814]/15 text-[#0fb013]", icon: Shield },
  lector: { label: "Lector", cls: "bg-black/[0.06] text-black/60", icon: Eye },
};

const inputCls =
  "w-full px-4 py-3 text-[13px] font-medium text-black bg-white border-2 border-black/10 rounded-xl focus:border-[#0fb013] focus:outline-none transition-colors duration-200";

const RoleBadge = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META.lector;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full ${meta.cls}`}>
      <Icon size={12} />
      {meta.label}
    </span>
  );
};

const TempPasswordBox = ({ email, password, onClose }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div data-testid="temp-password-box" className="bg-black rounded-2xl p-5 relative">
      <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={16} /></button>
      <p className="text-[12px] font-bold text-[#0fe814] uppercase tracking-wider">Contraseña temporal generada</p>
      <p className="mt-1 text-[12px] font-medium text-white/60">Compártela de forma segura con {email}. Solo se muestra una vez; deberá cambiarla al ingresar.</p>
      <div className="mt-3 flex items-center gap-2">
        <code data-testid="temp-password-value" className="flex-1 text-[16px] font-bold text-white bg-white/10 px-4 py-2.5 rounded-xl break-all">{password}</code>
        <button
          data-testid="copy-temp-password"
          onClick={() => { navigator.clipboard?.writeText(password); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="shrink-0 w-10 h-10 rounded-xl bg-[#0fe814] flex items-center justify-center text-black hover:bg-[#0fb013] transition-colors"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
    </div>
  );
};

const AdminUsers = ({ token, currentUser }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "admin" });
  const [creating, setCreating] = useState(false);
  const [tempPw, setTempPw] = useState(null); // {email, password}
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });

  const startEdit = (u) => { setEditing(u.id); setEditForm({ name: u.name || "", email: u.email }); setError(""); };

  const saveEdit = async (u) => {
    const payload = {};
    if (editForm.name.trim() !== (u.name || "")) payload.name = editForm.name.trim();
    if (editForm.email.trim().toLowerCase() !== u.email) payload.email = editForm.email.trim().toLowerCase();
    if (Object.keys(payload).length === 0) { setEditing(null); return; }
    await patchUser(u.id, payload, u.id + "edit");
    setEditing(null);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar definitivamente al usuario ${u.email}? Esta acción no se puede deshacer.`)) return;
    setBusy(u.id + "del");
    setError("");
    try {
      await axios.delete(`${API}/auth/users/${u.id}`, { headers });
      setUsers((list) => list.filter((x) => x.id !== u.id));
    } catch (err) {
      setError(err.response?.data?.detail || "Error eliminando usuario");
    } finally {
      setBusy(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/users`, { headers });
      setUsers(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/users`, form, { headers });
      setUsers((u) => [...u, res.data.user]);
      setTempPw({ email: res.data.user.email, password: res.data.temp_password });
      setShowForm(false);
      setForm({ name: "", email: "", role: "admin" });
    } catch (err) {
      setError(err.response?.data?.detail || "Error creando usuario");
    } finally {
      setCreating(false);
    }
  };

  const patchUser = async (userId, payload, busyKey) => {
    setBusy(busyKey);
    setError("");
    try {
      const res = await axios.patch(`${API}/auth/users/${userId}`, payload, { headers });
      setUsers((list) => list.map((u) => (u.id === userId ? res.data.user : u)));
      if (res.data.temp_password) {
        setTempPw({ email: res.data.user.email, password: res.data.temp_password });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error actualizando usuario");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-extrabold text-[19px] text-black">Usuarios del panel</h2>
          <p className="text-[12px] font-medium text-black/50">Gestiona accesos y roles del equipo</p>
        </div>
        <button
          data-testid="new-user-btn"
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center justify-center gap-2 text-[13px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-5 py-3 rounded-full transition-colors shadow-[0_4px_14px_rgba(15,232,20,0.3)]"
        >
          <UserPlus size={15} />
          Nuevo usuario
        </button>
      </div>

      {tempPw && <TempPasswordBox email={tempPw.email} password={tempPw.password} onClose={() => setTempPw(null)} />}

      {showForm && (
        <form onSubmit={createUser} data-testid="new-user-form" className="bg-white border border-black/[0.06] rounded-2xl p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="user-name-input" className={inputCls} placeholder="Nombre completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
          <input data-testid="user-email-input" type="email" className={inputCls} placeholder="Correo electrónico" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <select data-testid="user-role-input" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="admin">Admin — gestiona solicitudes</option>
            <option value="lector">Lector — solo visualiza</option>
            <option value="superadmin">Superadmin — control total</option>
          </select>
          <button data-testid="create-user-submit" type="submit" disabled={creating} className="inline-flex items-center justify-center gap-2 text-[13px] font-bold text-white bg-black hover:bg-black/85 px-5 py-3 rounded-xl transition-colors disabled:opacity-60">
            {creating ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
            Crear
          </button>
        </form>
      )}

      {error && <p data-testid="users-error" className="text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-4 py-2.5 rounded-xl">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={26} className="animate-spin text-black/30" /></div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const isSelf = u.id === currentUser.id;
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className={`bg-white border rounded-2xl p-4 sm:p-5 ${u.active ? "border-black/[0.06]" : "border-black/[0.06] opacity-55"}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-black text-[#0fe814] flex items-center justify-center font-extrabold text-[13px]">
                      {(u.name || u.email).split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      {editing === u.id ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input data-testid={`edit-name-${u.email}`} value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nombre"
                            className="text-[13px] font-bold text-black bg-white border-2 border-black/10 rounded-xl px-3 py-1.5 focus:border-[#0fb013] focus:outline-none" />
                          <input data-testid={`edit-email-${u.email}`} type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} placeholder="Correo"
                            className="text-[13px] font-semibold text-black bg-white border-2 border-black/10 rounded-xl px-3 py-1.5 focus:border-[#0fb013] focus:outline-none min-w-[220px]" />
                        </div>
                      ) : (
                        <>
                          <p className="text-[14px] font-bold text-black truncate">
                            {u.name} {isSelf && <span className="text-[10px] font-bold text-black/40">(tú)</span>}
                          </p>
                          <p className="text-[12px] font-medium text-black/50 truncate">{u.email}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RoleBadge role={u.role} />
                    {!u.active && <span className="text-[11px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-3 py-1 rounded-full">Inactivo</span>}
                    {u.must_change_password && <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">Contraseña temporal</span>}
                    <select
                      data-testid={`twofa-select-${u.email}`}
                      value={u.twofa_enabled === false ? "off" : u.preferred_2fa || "email"}
                      disabled={busy === u.id + "2fa"}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "off") { if (window.confirm(`¿Desactivar la verificación en dos pasos para ${u.email}? Podrá entrar solo con contraseña.`)) patchUser(u.id, { twofa_enabled: false }, u.id + "2fa"); return; }
                        patchUser(u.id, { twofa_enabled: true, preferred_2fa: v }, u.id + "2fa");
                      }}
                      title="Verificación en dos pasos"
                      className={`w-auto max-w-[190px] shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-full border-2 focus:outline-none ${u.twofa_enabled === false ? "border-[#ff603e]/40 text-[#ff603e] bg-[#ff603e]/5" : "border-black/10 text-black/70 bg-white"}`}
                    >
                      <option value="email">2FA: Correo</option>
                      <option value="totp" disabled={!u.totp_enabled}>2FA: Authenticator{!u.totp_enabled ? " (no configurado)" : ""}</option>
                      <option value="off">2FA: Desactivado</option>
                    </select>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {editing === u.id ? (
                      <>
                        <button data-testid={`save-edit-${u.email}`} onClick={() => saveEdit(u)} disabled={busy === u.id + "edit"}
                          className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-3 py-1.5 rounded-full transition-colors">
                          {busy === u.id + "edit" ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
                        </button>
                        <button data-testid={`cancel-edit-${u.email}`} onClick={() => setEditing(null)}
                          className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black/60 hover:text-black bg-black/[0.04] hover:bg-black/[0.08] px-3 py-1.5 rounded-full transition-colors">
                          <X size={12} /> Cancelar
                        </button>
                      </>
                    ) : (
                      <button data-testid={`edit-user-${u.email}`} onClick={() => startEdit(u)} title="Editar nombre y correo"
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black/60 hover:text-black bg-black/[0.04] hover:bg-black/[0.08] px-3 py-1.5 rounded-full transition-colors">
                        <Pencil size={12} /> Editar
                      </button>
                    )}
                  </div>

                  {!isSelf && (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        data-testid={`role-select-${u.email}`}
                        value={u.role}
                        disabled={busy === u.id + "role"}
                        onChange={(e) => patchUser(u.id, { role: e.target.value }, u.id + "role")}
                        className="text-[11px] font-bold text-black bg-white border-2 border-black/10 rounded-full px-3 py-1.5 focus:border-[#0fb013] focus:outline-none"
                      >
                        <option value="superadmin">Superadmin</option>
                        <option value="admin">Admin</option>
                        <option value="lector">Lector</option>
                      </select>
                      <button
                        data-testid={`reset-password-${u.email}`}
                        onClick={() => patchUser(u.id, { reset_password: true }, u.id + "pw")}
                        disabled={busy === u.id + "pw"}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black/60 hover:text-black bg-black/[0.04] hover:bg-black/[0.08] px-3 py-1.5 rounded-full transition-colors"
                        title="Generar nueva contraseña temporal"
                      >
                        <KeyRound size={12} /> Reset clave
                      </button>
                      <button
                        data-testid={`reset-2fa-${u.email}`}
                        onClick={() => patchUser(u.id, { reset_2fa: true }, u.id + "2fa")}
                        disabled={busy === u.id + "2fa"}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black/60 hover:text-black bg-black/[0.04] hover:bg-black/[0.08] px-3 py-1.5 rounded-full transition-colors"
                        title="Reiniciar 2FA (deberá configurarlo de nuevo)"
                      >
                        <RotateCcw size={12} /> Reset 2FA
                      </button>
                      <button
                        data-testid={`toggle-active-${u.email}`}
                        onClick={() => patchUser(u.id, { active: !u.active }, u.id + "act")}
                        disabled={busy === u.id + "act"}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors ${u.active ? "text-[#ff603e] bg-[#ff603e]/10 hover:bg-[#ff603e]/20" : "text-[#0fb013] bg-[#0fe814]/10 hover:bg-[#0fe814]/20"}`}
                      >
                        <Power size={12} /> {u.active ? "Desactivar" : "Activar"}
                      </button>
                      <button
                        data-testid={`delete-user-${u.email}`}
                        onClick={() => deleteUser(u)}
                        disabled={busy === u.id + "del"}
                        title="Eliminar usuario definitivamente"
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-[#ff603e] hover:bg-[#e04f2f] px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                      >
                        {busy === u.id + "del" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Borrar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
